<script lang="ts">
  import { autowalkRate, ceiling, medals } from '../../core/engine';
  import { primesUpTo } from '../../core/numbers';
  import Icon from '../../shared/components/Icon.svelte';
  import NumberLine from '../../shared/components/NumberLine.svelte';
  import NumberReadout from '../../shared/components/NumberReadout.svelte';
  import TapDial from '../components/TapDial.svelte';
  import TrialPanel from '../../shared/components/TrialPanel.svelte';
  import { fmt } from '../../shared/lib/format';
  import { game } from '../../shared/lib/game.svelte';

  let { onNavigate }: { onNavigate: (tab: 'upgrades') => void } = $props();

  const s = $derived(game.s);
  const auto = $derived(autowalkRate(s));
  const firstVisit = $derived(s.taps === 0 && s.lifetimePoints === 0);
  const atFirstHorizon = $derived(s.upgrades.horizon === 0 && s.maxVisited >= ceiling(s));
</script>

<section class="play">
  <div class="top">
    <NumberReadout />
  </div>

  <div class="center">
    <TapDial />
    <div class="controls">
      {#if s.upgrades.inverter > 0}
        <button class="chip" onclick={() => game.flip()} aria-label="Flip direction">
          <Icon name="flip" size={16} />
          {s.direction === 1 ? 'Walking up' : 'Walking down'}
        </button>
      {/if}
      {#if game.trial.phase === 'off'}
        <button class="chip" onclick={() => game.armTrial(game.trial.length)}>
          <Icon name="timer" size={16} /> Speed trial
        </button>
      {/if}
      {#if auto > 0}
        <span class="chip static"><span class="pulse"></span>Auto {auto}/s</span>
      {/if}
    </div>
  </div>

  <div class="bottom">
    {#if game.trial.phase !== 'off'}
      <TrialPanel />
    {/if}

    {#if s.upgrades.radar > 0}
      <NumberLine />
    {:else if firstVisit}
      <p class="coach">
        Tap the dial to walk the number line. Each new integer earns a point; each prime earns a
        medal.
      </p>
    {:else}
      <div class="meter" aria-label="Medals in reach">
        <span class="eyebrow">Prime medals</span>
        <span class="num"
          ><strong>{fmt(medals(s))}</strong> of {fmt(primesUpTo(ceiling(s)))} in reach</span
        >
      </div>
    {/if}

    {#if atFirstHorizon}
      <button class="nudge" onclick={() => onNavigate('upgrades')}>
        You've reached the first horizon. Open Upgrades to go further
        <Icon name="right" size={16} />
      </button>
    {/if}
  </div>
</section>

<style>
  .play {
    height: 100%;
    max-width: 640px;
    margin: 0 auto;
    padding: var(--space-5) var(--space-4) var(--space-4);
    display: grid;
    grid-template-rows: auto 1fr auto;
    gap: var(--space-4);
    min-height: 0;
  }

  .top {
    display: flex;
    justify-content: center;
    padding-top: clamp(0px, 3vh, 32px);
  }

  .center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    min-height: 0;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--space-2);
    min-height: 36px;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 0 14px;
    border-radius: 999px;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-2);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    transition:
      color var(--t) var(--ease-out),
      border-color var(--t) var(--ease-out);
  }
  button.chip:hover {
    color: var(--ink);
    border-color: color-mix(in srgb, var(--accent) 50%, var(--line));
  }
  .chip.static {
    background: transparent;
  }

  .pulse {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--accent);
    animation: pulse 1.6s ease-in-out infinite;
  }
  @keyframes pulse {
    50% {
      opacity: 0.3;
      transform: scale(0.8);
    }
  }

  .bottom {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .coach {
    text-align: center;
    color: var(--ink-2);
    font-size: var(--text-sm);
    max-width: 36ch;
    margin: 0 auto;
  }

  .meter {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius);
    background: var(--bg-sunken);
    font-size: var(--text-sm);
    color: var(--ink-2);
  }
  .meter strong {
    color: var(--prime);
  }

  .nudge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius);
    background: color-mix(in srgb, var(--accent) 12%, var(--bg-raised));
    border: 1px solid color-mix(in srgb, var(--accent) 40%, var(--line));
    color: var(--ink);
    font-size: var(--text-sm);
    font-weight: 600;
    animation: rise var(--t-slow) var(--ease-out);
  }
  @keyframes rise {
    from {
      transform: translateY(8px);
      opacity: 0;
    }
  }
</style>
